import { useState, type ReactNode } from "react";
import { Globe } from "lucide-react";

interface InlineLinkProps {
  href: string;
  children: ReactNode;
}

export const isHttpUrl = (href?: string): href is string => {
  if (!href) return false;

  try {
    const url = new URL(href);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

export const InlineLink = ({ href, children }: InlineLinkProps) => {
  const [failedFaviconUrl, setFailedFaviconUrl] = useState<string | null>(null);
  const faviconUrl = `${new URL(href).origin}/favicon.ico`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary dark:text-sky-600 hover:underline underline-offset-2 break-all"
    >
      {failedFaviconUrl === faviconUrl ? (
        <Globe aria-hidden="true" className="inline-block w-3.5 h-3.5 mr-1 align-[-2px]" />
      ) : (
        <img
          key={faviconUrl}
          src={faviconUrl}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          className="inline-block w-3.5 h-3.5 !my-0 mr-1 rounded-full align-[-2px]"
          onError={() => setFailedFaviconUrl(faviconUrl)}
        />
      )}
      {children}
    </a>
  );
};

const trimTrailingPunctuation = (value: string) => {
  let url = value;
  while (/[.,!?;:]$/.test(url)) url = url.slice(0, -1);

  for (const [opening, closing] of [["(", ")"], ["[", "]"], ["{", "}"]] as const) {
    while (
      url.endsWith(closing) &&
      url.split(closing).length - 1 > url.split(opening).length - 1
    ) {
      url = url.slice(0, -1);
    }
  }

  return { url, punctuation: value.slice(url.length) };
};

export const linkifyText = (text: string): ReactNode => {
  const urlPattern = /https?:\/\/[^\s<>"'`]+/gi;
  const nodes: ReactNode[] = [];
  let previousEnd = 0;
  let match: RegExpExecArray | null;

  while ((match = urlPattern.exec(text)) !== null) {
    const { url, punctuation } = trimTrailingPunctuation(match[0]);
    if (!isHttpUrl(url)) continue;

    nodes.push(text.slice(previousEnd, match.index));
    nodes.push(
      <InlineLink key={match.index} href={url}>
        {url}
      </InlineLink>,
    );
    if (punctuation) nodes.push(punctuation);
    previousEnd = match.index + match[0].length;
  }

  nodes.push(text.slice(previousEnd));
  return nodes;
};
